import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

interface IdentityErrorBannerProps {
  onRetry: () => void;
  onLogout: () => void;
  retrying: boolean;
}

/**
 * `GET /auth/current` failed. Shown over the running app rather than instead of
 * it (ENG-1316).
 *
 * The identity query is `retry: false` and treats any non-ok status as a
 * throw, so a single transient 500 — including one from a window-focus
 * refetch — used to replace the whole tree, tearing down the WebGPU viewer and
 * its WASM heap mid-session. Nothing about that failure requires unmounting:
 * the session is still live, every API call is still authorized server-side,
 * and consumers of `user` already handle it being null.
 */
export function IdentityErrorBanner({
  onRetry,
  onLogout,
  retrying,
}: IdentityErrorBannerProps) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="fixed inset-x-0 top-0 z-50 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-b border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm backdrop-blur"
    >
      <p className="text-foreground">
        <span className="font-semibold">
          {t('common.authUnavailableTitle')}
        </span>{' '}
        <span className="text-muted-foreground">
          {t('common.authUnavailableBody')}
        </span>
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" disabled={retrying} onClick={onRetry}>
          {t('common.retry')}
        </Button>
        <Button size="sm" variant="outline" onClick={onLogout}>
          {t('nav.logout')}
        </Button>
      </div>
    </div>
  );
}
